import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { PersistOptions } from "zustand/middleware";
import type {
  CompanySettings,
  GlobalSettings,
  Coordinates,
  NodeMapSettingType,
  NodeTableColumnSettings,
} from "~/types/ui";
import { DEFAULT_NODE_MAP_SETTINGS } from "~/utils/node-map-util";

export interface SettingState {
  companySettings: CompanySettings | null;
  globalSettings: GlobalSettings | null;

  setCompanySettings: (companySettings: CompanySettings) => void;
  setGlobalSettings: (globalSettings: GlobalSettings) => void;

  // Computed values
  getNodeTableColumns: () => NodeTableColumnSettings[];
  getCompanyCoordinates: () => Coordinates;
  getMapSettings: () => NodeMapSettingType;
  getNodeAlias: () => string | undefined;
  getLandingDashboardId: () => string | undefined;
  getDocumentTitle: () => string;
  getWhiteLabelImage: () => string | undefined;
  getGlobalWhiteLabelImage: () => string | undefined;

  reset: () => void;
}

const useSettingState = create<SettingState>()(
  persist(
    (set, get) => ({
      companySettings: null,
      globalSettings: null,

      setCompanySettings: (settings: object) =>
        set(() => ({ companySettings: settings })),

      setGlobalSettings: (settings: object) =>
        set(() => ({ globalSettings: settings })),

      getNodeTableColumns: () => {
        const ntcStr = get().companySettings?.NODE_TABLE_COLUMNS;
        if (!ntcStr) return defaultNodeTableColumns;

        try {
          let res = JSON.parse(ntcStr) as NodeTableColumnSettings[];
          if (res.length <= 0) return defaultNodeTableColumns;

          return res;
        } catch {
          return defaultNodeTableColumns;
        }
      },
      getCompanyCoordinates: () => {
        const cStr = get().companySettings?.COORDINATES;
        if (!cStr) return { latitude: null, longitude: null };

        try {
          return JSON.parse(cStr) as Coordinates;
        } catch {
          return { latitude: null, longitude: null };
        }
      },
      getMapSettings: () => {
        const nmsStr = get().companySettings?.MAPSETTINGS;
        if (!nmsStr) return DEFAULT_NODE_MAP_SETTINGS;

        try {
          return JSON.parse(nmsStr) as NodeMapSettingType;
        } catch {
          return DEFAULT_NODE_MAP_SETTINGS;
        }
      },

      getNodeAlias: () => get().companySettings?.NODE_ALIAS,

      getLandingDashboardId: () =>
        get().companySettings?.LANDING_DASHBOARD?.trim() || undefined,

      getWhiteLabelImage: () => get().companySettings?.WHITE_LABEL_IMAGE,

      getGlobalWhiteLabelImage: () => get().globalSettings?.WHITE_LABEL_IMAGE,

      getDocumentTitle: () => {
        const title =
          get().companySettings?.COMPANY_BRANDING_TITLE ||
          get().globalSettings?.branding;
        return title ? title : "SensorOnline";
      },

      reset: () =>
        set(() => ({
          companySettings: null,
        })),
    }),
    {
      name: "setting-state",
    } as PersistOptions<SettingState>,
  ),
);

export const defaultNodeTableColumns: NodeTableColumnSettings[] = [
  {
    index: 0,
    title: "Node Type",
    key: "nodeType",
    subgroup: "Default",
    isDefault: true,
    isAttribute: false,
    useLangLine: true,
    langLine: "tag.fields.nodeType",
    sortable: null,
  },
  {
    index: 1,
    title: "Name",
    key: "name",
    subgroup: "Default",
    isDefault: true,
    isAttribute: false,
    useLangLine: true,
    langLine: "tag.fields.name",
    sortable: null,
  },
  {
    index: 2,
    title: "DevEUI",
    key: "deveui",
    subgroup: "Default",
    isDefault: true,
    isAttribute: false,
    useLangLine: true,
    langLine: "tag.fields.deveui",
    sortable: null,
  },
  {
    index: 2,
    title: "Application",
    key: "application",
    subgroup: "Default",
    isDefault: true,
    isAttribute: false,
    useLangLine: true,
    langLine: "tag.fields.application",
    sortable: null,
  },
  {
    index: 3,
    title: "Type",
    key: "decoder",
    subgroup: "Default",
    isDefault: true,
    isAttribute: false,
    useLangLine: true,
    langLine: "tag.fields.type",
    sortable: null,
  },
  {
    index: 4,
    title: "RSSI",
    key: "rssi",
    subgroup: "Default",
    isDefault: true,
    isAttribute: false,
    useLangLine: false,
    langLine: null,
    sortable: null,
  },
  {
    index: 5,
    title: "Last Contact",
    key: "lastContact",
    subgroup: "Default",
    isDefault: true,
    isAttribute: false,
    useLangLine: true,
    langLine: "tag.fields.latestValue",
    sortable: null,
  },
  {
    index: 6,
    title: "Aktiv",
    key: "active",
    subgroup: "Default",
    isDefault: true,
    isAttribute: false,
    useLangLine: true,
    langLine: "tag.fields.activeData",
    sortable: false,
  },
];

export default useSettingState;
